import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Send, Sparkles, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import api, { API, getToken } from "@/lib/api";
import { ProposalCard, proposalToPayload } from "@/components/calendar/BulkEntryDialog";

const MARK = "__AIRTIME_EVENTS__";

const SUGGESTIONS = [
  "Qu'est-ce que j'ai d'urgent cette semaine ?",
  "Quels clients attendent une réponse ?",
  "Trouve-moi 4 heures pour aller jouer au golf.",
  "Ajoute dentiste mardi de 14h à 15h.",
  "Bloque vendredi matin.",
  "Libère-moi jeudi après-midi.",
];

function EventProposals({ msg, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      await api.post("/calendar/events/bulk", { events: msg.proposals.map(proposalToPayload) });
      toast.success(`${msg.proposals.length} événement(s) créé(s) dans le calendrier AIRTIME`);
      onUpdate({ ...msg, confirmed: true });
    } catch { toast.error("Création impossible"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 mt-2" data-testid="ask-event-proposals">
      {msg.proposals.map((p, i) => <ProposalCard key={i} p={p} onChange={(np) => onUpdate({ ...msg, proposals: msg.proposals.map((x, j) => j === i ? np : x) })} />)}
      {msg.confirmed ? <div className="inline-flex items-center gap-1.5 text-xs text-emerald-400"><Check className="w-3.5 h-3.5" /> Ajouté à ton agenda</div> : (
        <button data-testid="ask-confirm-events" onClick={confirm} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-xs text-primary-foreground font-medium disabled:opacity-50">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Confirmer {msg.proposals.length > 1 ? "tout" : ""}</button>
      )}
    </div>
  );
}

export default function Ask() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);
  const sessionId = useRef(`sess_${Date.now()}`);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (q) => {
    const question = (q ?? input).trim();
    if (!question || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: question }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}) },
        credentials: "include",
        body: JSON.stringify({ question, session_id: sessionId.current }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "", proposals = null;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        if (buffer.startsWith(MARK) && !proposals) {
          const nl = buffer.indexOf("\n");
          if (nl === -1) continue;
          try { proposals = JSON.parse(buffer.slice(MARK.length, nl)).events || []; } catch { proposals = []; }
          buffer = buffer.slice(nl + 1);
        } else if (MARK.startsWith(buffer.slice(0, MARK.length)) && buffer.length < MARK.length && !proposals) {
          continue; // still deciding whether this is a marker
        }
        const text = buffer;
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: text, proposals: proposals || undefined };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Désolé, une erreur est survenue." };
        return copy;
      });
    } finally { setStreaming(false); }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] md:h-[calc(100vh-5rem)]">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Assistant AI</h1>
        <p className="text-muted-foreground mt-1 text-sm">Pose une question en langage naturel — l'assistant connaît tes tâches, emails et agenda.</p>
      </div>

      <div className="flex-1 overflow-y-auto airtime-scroll mt-6 space-y-4 pr-1" data-testid="ask-messages">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-4"><Sparkles className="w-6 h-6" /></div>
            <div className="text-muted-foreground text-sm mb-6 max-w-sm">Essaie une de ces questions :</div>
            <div className="flex flex-wrap gap-2.5 justify-center max-w-xl">
              {SUGGESTIONS.map((s) => (
                <button key={s} data-testid={`ask-suggestion-${s.slice(0, 12)}`} onClick={() => send(s)}
                  className="rounded-full border border-border px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:border-primary/40 transition-all">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap
              ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-card border border-border"}`}>
              {m.content || (streaming && i === messages.length - 1 ? <Loader2 className="w-4 h-4 animate-spin" /> : "")}
              {m.proposals && <EventProposals msg={m} onUpdate={(nm) => setMessages((all) => all.map((x, j) => j === i ? nm : x))} />}
            </div>
          </motion.div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="mt-4 flex items-end gap-3">
        <textarea
          data-testid="ask-input"
          rows={1}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Demande-moi quelque chose…"
          className="flex-1 resize-none bg-card border border-border rounded-2xl px-4 py-3 text-sm max-h-32 airtime-scroll"
        />
        <button data-testid="ask-send" onClick={() => send()} disabled={streaming || !input.trim()}
          className="rounded-2xl bg-primary text-primary-foreground p-3.5 hover:opacity-90 transition-all disabled:opacity-50">
          {streaming ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
}
